import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, LinearProgress, Skeleton, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";

import CopilotIcon from "components/shared/CopilotIcon/CopilotIcon";
import { getBudgetBarColor, getBudgetTextColor } from "pages/admin/components/CopilotDashboard/budgetColors";
import { useLazyGetCopilotUserDetailQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useGetUserBudgetQuery } from "state/rtkQuery/user/userQueries";
import { formatLocalDateTime } from "utils/formatLocalDateTime";

const ACTIVITY_WINDOW_DAYS = 30;

const formatUsd = (value) =>
  Number(value ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SCOPE_LABEL = { user: "User budget", cost_center: "Cost center budget", enterprise: "Enterprise budget" };

// The Copilot details for one person, across every organization: their budget, and when they last used Copilot.
//
// Budget and "licensed in" come from the person's budget summary, which needs no organization. The summary lists the
// organizations they hold a Copilot license in; usage is per organization, so the last activity is looked up in each
// of those (usually one) and the most recent is shown.
// `onLicensedOrgs` hands the licensed organization ids up so the memberships table can mark them with a Copilot icon.
const CopilotSummary = ({ email, platformId, orgIdToName = {}, onLicensedOrgs }) => {
  const { data, isFetching, isError, error } = useGetUserBudgetQuery({ email, platformId }, { skip: !email || !platformId });
  const licensedOrgIds = useMemo(() => data?.licensed_organizations ?? [], [data]);

  useEffect(() => {
    onLicensedOrgs?.(licensedOrgIds);
  }, [licensedOrgIds]); // eslint-disable-line

  // Last activity: one usage lookup per licensed organization, keeping the most recent.
  const [fetchDetail] = useLazyGetCopilotUserDetailQuery();
  const [activity, setActivity] = useState({ loading: false, at: null, orgId: null, failed: false });
  const licensedKey = licensedOrgIds.join(",");
  useEffect(() => {
    if (!email || !platformId || !licensedOrgIds.length) {
      setActivity({ loading: false, at: null, orgId: null, failed: false });
      return undefined;
    }
    let cancelled = false;
    setActivity({ loading: true, at: null, orgId: null, failed: false });
    const toDate = dayjs().format("YYYY-MM-DD");
    const fromDate = dayjs().subtract(ACTIVITY_WINDOW_DAYS, "day").format("YYYY-MM-DD");
    Promise.all(
      licensedOrgIds.map((orgId) =>
        fetchDetail({ email, orgId, platformId, fromDate, toDate }, true).then((result) => ({ orgId, result }))
      )
    ).then((results) => {
      if (cancelled) return;
      let latest = { at: null, orgId: null };
      let failed = false;
      results.forEach(({ orgId, result }) => {
        // A 404 just means no usage in the window for that organization.
        if (result.isError && result.error?.status !== 404) failed = true;
        const at = result.data?.budget?.last_activity_at;
        if (at && (!latest.at || dayjs(at).isAfter(dayjs(latest.at)))) latest = { at, orgId };
      });
      setActivity({ loading: false, at: latest.at, orgId: latest.orgId, failed: failed && !latest.at });
    });
    return () => {
      cancelled = true;
    };
  }, [email, platformId, licensedKey]); // eslint-disable-line

  const effective = data?.effective_budget;
  const effectiveDetail = (data?.budgets ?? []).find((b) => b.id === effective?.id);
  const hasBudget = Boolean(effective && effective.budget_amount > 0);
  const pct = hasBudget ? Math.min((effective.consumed_amount / effective.budget_amount) * 100, 100) : 0;
  const over = hasBudget && effective.consumed_amount > effective.budget_amount;
  const barColor = getBudgetBarColor(pct);
  const openRequest = data?.open_request && Object.keys(data.open_request).length > 0 ? data.open_request : null;
  const licensed = licensedOrgIds.length > 0;

  return (
    <Box data-testid="copilot-summary" sx={{ border: "1px solid #edf1f4", borderRadius: "10px", p: "12px 14px", background: "#fcfdfd", boxSizing: "border-box", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <CopilotIcon aria-label="Copilot" sx={{ fontSize: 18, color: "#1a1a1a" }} />
        <Typography sx={{ fontWeight: 700, color: "#0b756c", fontSize: 13 }}>GitHub Copilot</Typography>
        {!isFetching && !isError && (
          <Chip
            size="small"
            label={licensed ? `Licensed in ${licensedOrgIds.length} org${licensedOrgIds.length === 1 ? "" : "s"}` : "No Copilot license"}
            sx={{
              ml: "auto",
              height: 20,
              fontSize: "0.68rem",
              fontWeight: 700,
              backgroundColor: licensed ? "#e6f4ea" : "#f0f0f0",
              color: licensed ? "#22A06B" : "#888",
            }}
          />
        )}
      </Box>

      {isFetching ? (
        <>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rounded" height={8} sx={{ my: 1 }} />
          <Skeleton variant="text" width="45%" />
        </>
      ) : isError ? (
        <Typography sx={{ fontSize: 12.5, color: "#999", fontStyle: "italic" }}>
          {error?.status === 403 ? "You don't have access to this person's Copilot details." : "Couldn't load the Copilot details."}
        </Typography>
      ) : (
        <>
          <Typography sx={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3 }}>
            {(SCOPE_LABEL[effectiveDetail?.budget_scope] ?? "Budget").toUpperCase()}
          </Typography>
          {hasBudget ? (
            <Box sx={{ mt: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: "#444" }}>
                  {formatUsd(effective.consumed_amount)} of {formatUsd(effective.budget_amount)}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: getBudgetTextColor(barColor) }}>{pct.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                aria-label="Budget used"
                sx={{ height: 8, borderRadius: 8, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 8 } }}
              />
              <Typography sx={{ fontSize: 11.5, mt: 0.5, color: over ? "#E2231A" : "#999", fontWeight: over ? 600 : 400 }}>
                {over ? `Over budget by ${formatUsd(effective.consumed_amount - effective.budget_amount)}` : `${formatUsd(effective.budget_amount - effective.consumed_amount)} remaining`}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#999", fontStyle: "italic", mt: 0.5 }}>No budget set</Typography>
          )}

          {openRequest && (
            <Chip
              size="small"
              label={`Open request: ${openRequest.action ?? "change"}${openRequest.requested_amount != null ? ` ${formatUsd(openRequest.requested_amount)}` : ""}`}
              sx={{ mt: 1, height: 20, fontSize: "0.68rem", backgroundColor: "#fff8e1", color: "#b38600" }}
            />
          )}

          <Typography sx={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3, mt: 1.5 }}>LAST COPILOT ACTIVITY</Typography>
          {activity.loading ? (
            <Skeleton variant="text" width="55%" />
          ) : activity.at ? (
            <Typography sx={{ fontSize: 13, color: "#1f2328", fontWeight: 600 }} data-testid="copilot-last-activity">
              {formatLocalDateTime(activity.at)}
              {licensedOrgIds.length > 1 && activity.orgId && (
                <Typography component="span" sx={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
                  {" "}in {orgIdToName[activity.orgId] || activity.orgId}
                </Typography>
              )}
            </Typography>
          ) : (
            <Tooltip title={`Looked back ${ACTIVITY_WINDOW_DAYS} days`}>
              <Typography sx={{ fontSize: 13, color: "#999", fontStyle: "italic" }} data-testid="copilot-last-activity">
                {!licensed ? "—" : activity.failed ? "Couldn't load" : `No activity in the last ${ACTIVITY_WINDOW_DAYS} days`}
              </Typography>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};

export default CopilotSummary;
